import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Formik, Form, FormikProps } from "formik"
import SidePanel from "../../side-panel"
import {
  useToast,
  useValidateEmailAndNMLS,
  usePendingSubscriptionsCount
} from "../../../hooks"
import {
  CreatePendingSubscriptionPayload,
  PendingSubscriptionFull,
  UpdatePendingSubscriptionPayload,
  GooglePlace
} from "../../../../types"
import {
  convertAddressObjToPlaceString,
  convertGooglePlaceToAddressObj,
  validateZipcodeForPayload
} from "../../../utils"
import {
  useCreatePendingSubscriptionMutation,
  useUpdatePendingSubscriptionMutation
} from "../../../../redux/queries"
import { useSearchLoanOfficerQuery } from "../../../../redux/queries"
import ManageEngagementBasic from "../../../../common/ui/engagements/manage/sections/basic"
import ManageEngagementSelectors from "../../../../common/ui/engagements/manage/sections/selectors"
import ManageEngagementMisc from "../../../../common/ui/engagements/manage/sections/misc"
import { useAppSelector } from "../../../hooks"
import { selectUserDetails } from "../../../../redux/slices/auth"
import { dreamRateFormSchema } from "./dream-rate-form-schema"

type Props = {
  open: boolean
  data?: PendingSubscriptionFull
  onClose: () => void
}

const convertPendingSubscriptionToInitialValues = (
  data?: PendingSubscriptionFull,
  loid?: string
): CreatePendingSubscriptionPayload => {
  const location = data?.quote_info?.location
  const locationObj = {
    city: location?.city ?? "",
    county: location?.county ?? "",
    state: location?.state ?? "",
    street_address: location?.street_address ?? "",
    zipcode: location?.zipcode ?? ""
  }

  return {
    // Basic fields
    active: "1" as const,
    deleted: "0" as const,
    email: data?.email ?? "",
    first_name: data?.first_name ?? "",
    last_name: data?.last_name ?? "",
    phone: data?.phone ?? "",
    loid: data?.loid ?? loid ?? "",

    // Loan scenario fields
    loan_type: data?.quote_info?.loan_type ?? "conventional",
    loan_term: data?.quote_info?.loan_term?.toString() ?? "30",
    arm_term: data?.quote_info?.arm_term?.toString() ?? "",
    loan_purpose: data?.quote_info?.loan_purpose ?? "purchase",
    loan_amount: data?.quote_info?.loan_amount?.toString() ?? "",
    credit_score: data?.quote_info?.credit_score ?? 700,
    property_type: data?.quote_info?.property_type ?? "single_family_home",
    residency_type: data?.quote_info?.residency_type ?? "primary_home",
    list_price: data?.quote_info?.list_price?.toString() ?? "",
    cash_out: data?.quote_info?.cash_out?.toString() ?? "",
    first_time_homebuyer: false,
    va_funding_fee_type:
      data?.quote_info?.loan_type === "va"
        ? data?.quote_info?.va_funding_fee_type ?? ""
        : "",

    // Location fields
    ...locationObj,
    location: convertAddressObjToPlaceString(locationObj),

    // Admin fields
    taxes: data?.quote_info?.admin_fields?.taxes ?? 0,
    insurance: data?.quote_info?.admin_fields?.insurance ?? 0,
    seller_credit: data?.quote_info?.admin_fields?.seller_credit ?? 0,

    // Extra flags
    waive_escrows: data?.quote_info?.waive_escrows ?? false,

    // Rate drop specific
    trigger_rate: data?.trigger_rate ?? 0,
    client_request_id: data?.client_request_id ?? undefined
  } as CreatePendingSubscriptionPayload
}

const ManageDreamRatePanel: FC<Props> = ({ open, data, onClose }) => {
  const { pushErrorToast, pushSuccessToast } = useToast()
  const formRef = useRef<FormikProps<CreatePendingSubscriptionPayload>>(null)
  const isCreateMode = !data?.id
  const { maxReached } = usePendingSubscriptionsCount()

  const [createPending, createState] = useCreatePendingSubscriptionMutation()
  const [updatePending, updateState] = useUpdatePendingSubscriptionMutation()
  const { validateSubscriptionEmail } = useValidateEmailAndNMLS()

  const userDetails = useAppSelector(selectUserDetails)
  const selectedBrandId = userDetails?.brand_id

  const [googlePlace, setGooglePlace] = useState<GooglePlace>()
  const [selectedLoanOfficer, setSelectedLoanOfficer] = useState<any>(null)

  // Reset mutation states when panel closes to prevent stale toast messages
  useEffect(() => {
    if (!open) {
      createState.reset()
      updateState.reset()
    }
  }, [open, createState, updateState])

  // Fetch loan officer details when selected
  const { data: loanOfficerData } = useSearchLoanOfficerQuery(
    { loid: selectedLoanOfficer?.loid ?? "" },
    { skip: !selectedLoanOfficer?.loid }
  )

  const initialValues = useMemo(
    () => convertPendingSubscriptionToInitialValues(data, userDetails?.id),
    [data, userDetails?.id]
  )

  const isLoading = createState.isLoading || updateState.isLoading

  const setFormField = useCallback((name: string, value: any) => {
    if (formRef.current) {
      formRef.current.setFieldValue(name, value)
    }
  }, [])

  const handleLoanOfficerChange = useCallback(
    (loid: string) => {
      setFormField("loid", loid)
      // We'll need to fetch the loan officer details to get the NMLS
      // For now, we'll store the loid and fetch the details
      setSelectedLoanOfficer({ loid })
    },
    [setFormField]
  )

  // Update form fields when Google Place is selected
  useEffect(() => {
    if (googlePlace) {
      const address = convertGooglePlaceToAddressObj(googlePlace)
      setFormField("city", address.city ?? "")
      setFormField("county", address.county ?? "")
      setFormField("state", address.state ?? "")
      setFormField("street_address", address.street_address ?? "")
      setFormField("zipcode", address.zipcode ?? "")
    }
  }, [googlePlace, setFormField])

  const onSubmit = useCallback(async () => {
    const values = formRef.current?.values
    if (!values) return

    // Minimal front-end validation
    if (!values.email || !values.trigger_rate) {
      pushErrorToast("Email and Trigger rate are required")
      return
    }

    // Zipcode validation - only required when location details are shown
    const zipcodeErrors = validateZipcodeForPayload(values, googlePlace)
    if (zipcodeErrors.zipcode) {
      pushErrorToast(zipcodeErrors.zipcode)
      return
    }

    if (isCreateMode) {
      // Extract NMLS from selected loan officer
      const selectedLO = loanOfficerData?.[0]
      const nmls = selectedLO?.nmls || ""

      // Send the full form values with NMLS - API wrapper will transform to backend format
      const payloadWithNMLS = {
        ...values,
        nmls
      }
      await createPending(payloadWithNMLS)
    } else if (data?.id) {
      const payload: UpdatePendingSubscriptionPayload = {
        id: data.id,
        ...values
      }
      await updatePending(payload)
    }
  }, [
    createPending,
    data?.id,
    isCreateMode,
    pushErrorToast,
    updatePending,
    loanOfficerData,
    googlePlace
  ])

  useEffect(() => {
    const success = isCreateMode
      ? createState.isSuccess && (createState.data as any)?.status === "success"
      : updateState.isSuccess && (updateState.data as any)?.status === "success"
    const error = isCreateMode ? createState.isError : updateState.isError

    if (success) {
      // Close panel immediately before showing toast and triggering refetch
      onClose()
      pushSuccessToast(
        `Successfully ${isCreateMode ? "created" : "updated"} dream rate`
      )
    } else if (error) {
      pushErrorToast(
        `Failed to ${isCreateMode ? "create" : "update"} dream rate`
      )
    }
  }, [
    createState.isError,
    createState.isSuccess,
    createState.data,
    isCreateMode,
    onClose,
    pushErrorToast,
    pushSuccessToast,
    updateState.isError,
    updateState.isSuccess,
    updateState.data
  ])

  const validate = useCallback(
    (values: CreatePendingSubscriptionPayload) => {
      const errors: any = {}

      // Email validation (existing)
      const emailErrors = validateSubscriptionEmail(
        // Reuse email validation from subscriptions
        { email: values.email, nmls: "" } as any,
        ""
      )
      if (emailErrors) {
        Object.assign(errors, emailErrors)
      }

      // Zipcode validation - only required when location details are shown
      const zipcodeErrors = validateZipcodeForPayload(values, googlePlace)
      Object.assign(errors, zipcodeErrors)

      return errors
    },
    [validateSubscriptionEmail, googlePlace]
  )

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={dreamRateFormSchema}
      onSubmit={onSubmit}
      validateOnBlur
      validateOnChange
      validate={validate}
      enableReinitialize
      innerRef={formRef}
    >
      {({ isValid, values, errors, dirty }) => {
        console.log("errors", errors)
        console.log("values", values)
        return (
          <SidePanel
            title={`${isCreateMode ? "Add new" : "Edit"} dream rate`}
            open={open}
            onClose={onClose}
            actions={[
              {
                label: isCreateMode ? "Add dream rate" : "Update dream rate",
                type: "primary",
                onClick: onSubmit,
                disabled:
                  isLoading ||
                  !isValid ||
                  !dirty ||
                  (isCreateMode && maxReached)
              }
            ]}
          >
            <Form>
              <ManageEngagementBasic
                values={values}
                loSearchParams={{ brand_id: selectedBrandId }}
                onlyAdmins={false}
                setFormField={setFormField}
                setGooglePlace={setGooglePlace}
                onLoanOfficerChange={handleLoanOfficerChange}
                hideStatusField
                googlePlace={googlePlace}
              />

              <ManageEngagementSelectors
                values={values}
                setFormField={setFormField}
                hasDreamRate
              />
              <ManageEngagementMisc
                values={values}
                setFormField={setFormField}
                hasWaiveEscrows
              />
            </Form>
          </SidePanel>
        )
      }}
    </Formik>
  )
}

export default ManageDreamRatePanel
